import React, { useRef, useState, useEffect, useMemo } from "react";
import ChatFooter from "./ask-ai-chat-footer";
import rhybusloader from "../../assets/images/anew-logo.svg"
import { RHYBUS_ACCESS_TOKEN } from "../../Configuration";
import MarkdownRenderer from "./markdown-renderer";
import { v4 as uuidv4 } from 'uuid';
import { CHAT_ID_PARAM, PENDING_CHAT_KEY } from "../../shared/constants/globalConstants";


const STORAGE_KEY = 'clutch_bot_history_v1';
const ACTIVE_CHAT_GROUP_ID_KEY = 'clutch_bot_active_chat_group';

const chatDBApi = {
    getHeaders: () => {
        const token = localStorage.getItem(RHYBUS_ACCESS_TOKEN) || sessionStorage.getItem(RHYBUS_ACCESS_TOKEN);
        return {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
        };
    },
    loadHistory: async (chatGroupId: string) => {
        return fetch(`${process.env.REACT_APP_ANEW_NLP_API_URL}/chat/messages/${chatGroupId}`, {
            headers: chatDBApi.getHeaders()
        }).then(res => res.json());
    },
    createMessages: async (chatGroupId: string, messages: any[]) => {
        return fetch(`${process.env.REACT_APP_ANEW_NLP_API_URL}/chat/messages`, {
            method: 'POST',
            headers: chatDBApi.getHeaders(),
            body: JSON.stringify({ chat_group_id: chatGroupId, messages })
        });
    },
    completeStream: async (msgId: string, content: string, versionIndex: number) => {
        return fetch(`${process.env.REACT_APP_ANEW_NLP_API_URL}/chat/messages/${msgId}/complete`, {
            method: 'PATCH',
            headers: chatDBApi.getHeaders(),
            body: JSON.stringify({ content, version_index: versionIndex })
        });
    },
    addVersion: async (msgId: string, content: string, versionIndex: number, isStreaming: boolean) => {
        return fetch(`${process.env.REACT_APP_ANEW_NLP_API_URL}/chat/messages/${msgId}/versions`, {
            method: 'POST',
            headers: chatDBApi.getHeaders(),
            body: JSON.stringify({ content, version_index: versionIndex, is_streaming: isStreaming })
        });
    },
    updateCurrentVersion: async (msgId: string, currentVersion: number) => {
        return fetch(`${process.env.REACT_APP_ANEW_NLP_API_URL}/chat/messages/${msgId}/version`, {
            method: 'PATCH',
            headers: chatDBApi.getHeaders(),
            body: JSON.stringify({ current_version: currentVersion })
        });
    }
};

let isPageUnloading = false;
if (typeof window !== 'undefined') {
    window.addEventListener('beforeunload', () => {
        isPageUnloading = true;
        try {
            const saved = localStorage.getItem(STORAGE_KEY);
            if (saved) {
                const parsed = JSON.parse(saved);
                let changed = false;
                const streams = (window as any).clutchBotStreams;
                if (streams) {
                    parsed.messages.forEach((msg: any) => {
                        if (msg.isStreaming) {
                            const stream = streams.get(msg.id);
                            if (stream && stream.buffer) {
                                const vIndex = msg.versions ? msg.versions.length - 1 : 0;
                                msg.versions[vIndex] = stream.buffer;
                                changed = true;
                            }
                        }
                    });
                    if (changed) {
                        localStorage.setItem(STORAGE_KEY, JSON.stringify(parsed));
                    }
                }
            }
        } catch (e) {}
    });
}

const getActiveStreams = () => {
    if (typeof window !== 'undefined') {
        if (!(window as any).clutchBotStreams) {
            (window as any).clutchBotStreams = new Map<string, { buffer: string, controller: AbortController }>();
        }
        return (window as any).clutchBotStreams;
    }
    return new Map<string, { buffer: string, controller: AbortController }>();
};
const activeStreams = getActiveStreams();

const EditableMessage = ({ initialText, onSave, onCancel }: { initialText: string, onSave: (txt: string) => void, onCancel: () => void }) => {
    const [text, setText] = useState(initialText);

    return (
        <div className="edit-message-container">
            <textarea
                className="edit-textarea"
                value={text}
                onChange={(e) => setText(e.target.value)}
                autoFocus
            />
            <div className="edit-footer">
                <button className="btn-cancel-edit" onClick={onCancel}>Cancel</button>
                <button className="btn-save-edit" onClick={() => onSave(text)}>
                    Send <span className="icon-rb-arrow-line-up-thick font-12"></span>
                </button>
            </div>
        </div>
    );
};

export default function AIChatBody() {
    const [messages, setMessages] = useState<any[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    // Only store the ID in LocalStorage, not the messages
    const [chatGroupId, setChatGroupId] = useState<string | null>(() => {
        try {
            return localStorage.getItem(ACTIVE_CHAT_GROUP_ID_KEY);
        } catch (e) {
            return null;
        }
    });

    // Keep the active chat ID saved across tabs/refreshes
    useEffect(() => {
        if (chatGroupId) {
            localStorage.setItem(ACTIVE_CHAT_GROUP_ID_KEY, chatGroupId);
        } else {
            localStorage.removeItem(ACTIVE_CHAT_GROUP_ID_KEY);
        }
    }, [chatGroupId]);

    const [message, setMessage] = useState("");
    const listRef = useRef<HTMLDivElement | null>(null);
    const ChatfooterRef = useRef<any>(null);
    const [copiedId, setCopiedId] = useState<string | null>(null);
    const [isStreaming, setIsStreaming] = useState(false);
    const[editingMessageId, setEditingMessageId] = useState<string | null>(null);

    const isMounted = useRef(true);
    const isNewChatCreated = useRef(false);

    const visibleMessages = useMemo(() => {
        return messages.filter(msg => {
            if (!msg.branchMap) return true;
            for (const [id, requiredVersion] of Object.entries(msg.branchMap)) {
                const parentMsg = messages.find(m => m.id === id);
                if (parentMsg && parentMsg.currentVersion !== requiredVersion) {
                    return false;
                }
            }
            return true;
        });
    }, [messages]);

    const lastVisibleMsg = visibleMessages[visibleMessages.length - 1];
    const isCurrentBranchStreaming = lastVisibleMsg ? !!lastVisibleMsg.isStreaming : false;
    const isCurrentBranchLoading = lastVisibleMsg ? (!!lastVisibleMsg.isStreaming && !lastVisibleMsg.recoveryText && (!lastVisibleMsg.versions[lastVisibleMsg.currentVersion] || lastVisibleMsg.versions[lastVisibleMsg.currentVersion].length === 0)) : false;

    useEffect(() => {
        isMounted.current = true;
        return () => {
            isMounted.current = false;
        };
    },[]);

    useEffect(() => {
        // Listen for background streams finishing when you change pages
        const handleStreamFinish = (e: any) => {
            const { id, text } = e.detail;
            setMessages(prev => prev.map(msg => {
                if (msg.id === id) {
                    const newVersions = [...msg.versions];
                    newVersions[newVersions.length - 1] = text;
                    return { ...msg, versions: newVersions, isStreaming: false, recoveryText: undefined };
                }
                return msg;
            }));
            if (activeStreams.size === 0) setIsStreaming(false);
        };
        window.addEventListener('stream-finished', handleStreamFinish);
        return () => window.removeEventListener('stream-finished', handleStreamFinish);
    },[]);
     
    useEffect(() => {
        let interval: any;
        if (isStreaming) {
            interval = setInterval(() => {
                if (!isMounted.current) return;

                if (activeStreams.size === 0) {
                    setIsStreaming(false);
                    return;
                }

                setMessages(prev => {
                    let changed = false;
                    const newArr =[...prev];

                    for (const [msgId, stream] of activeStreams.entries()) {
                        const msgIndex = newArr.findIndex(m => m.id === msgId);
                        if (msgIndex === -1) continue;
                        
                        const msg = newArr[msgIndex];
                        const targetIndex = msg.versions.length - 1;
                        const currentContent = msg.versions[targetIndex];

                        if (msg.recoveryText && stream.buffer.length === 0) {
                            continue;
                        }

                        if (currentContent !== stream.buffer) {
                            const newVersions = [...msg.versions];
                            newVersions[targetIndex] = stream.buffer; 
                            
                            newArr[msgIndex] = { ...msg, versions: newVersions, isStreaming: true, recoveryText: undefined };
                            changed = true;
                        }
                    }
                    return changed ? newArr : prev;
                });
            }, 75);
        }
        return () => clearInterval(interval);
    }, [isStreaming]);


    useEffect(() => {
        const token = localStorage.getItem(RHYBUS_ACCESS_TOKEN) || sessionStorage.getItem(RHYBUS_ACCESS_TOKEN);
        if (!token) {
            handleClearHistory();
        }
    },[]);

    const handleClearHistory = () => {
        activeStreams.forEach(stream => stream.controller.abort());
        activeStreams.clear();

        setMessages([]);
        setChatGroupId(null);
        setEditingMessageId(null);
        localStorage.removeItem(STORAGE_KEY);
        sessionStorage.removeItem('clutch_bot_scroll_pos');
        setIsStreaming(false);
    };

    const scrollToNewMessage = (msgId: string) => {
        setTimeout(() => {
            const element = document.getElementById(`user-msg-${msgId}`);
            if (element) {
                element.scrollIntoView({ behavior: 'smooth', block: 'start' });
            } else if (listRef.current) {
                listRef.current.scrollTop = listRef.current.scrollHeight;
            }
        }, 100);
    };

    useEffect(() => {
        if (messages.length > 0) {
             if (listRef.current) {
                 setTimeout(() => {
                    const savedScroll = sessionStorage.getItem('clutch_bot_scroll_pos');
                    const isAnyStreaming = messages.some(m => m.isStreaming);
                    
                    if (savedScroll !== null && !isAnyStreaming) {
                        listRef.current!.scrollTop = parseInt(savedScroll, 10);
                    } else {
                        listRef.current!.scrollTop = listRef.current!.scrollHeight;
                    }
                 }, 150);
             }
        }
    }, [messages.length === 0]);


    const formatTableToASCII = (tableBlock: string) => {
        const rows = tableBlock.trim().split('\n').map(row => 
            row.split('|').map(cell => cell.trim()).filter((cell, index, arr) => {
                return index !== 0 && index !== arr.length - 1 || cell !== "";
            })
        );
        const contentRows = rows.filter(row => !row.every(cell => /^[-:]+$/.test(cell)));
        if (contentRows.length === 0) return tableBlock;

        const MAX_COL_WIDTH = 50; 

        const colWidths = contentRows[0].map((_, colIndex) => {
            const maxContentLen = Math.max(...contentRows.map(row => (row[colIndex] || "").length));
            return Math.min(maxContentLen, MAX_COL_WIDTH);
        });

        const wrapText = (text: string, maxWidth: number): string[] => {
            if (text.length <= maxWidth) return[text];
            const words = text.split(' ');
            let lines: string[] =[];
            let currentLine = words[0];
            for (let i = 1; i < words.length; i++) {
                if (currentLine.length + 1 + words[i].length <= maxWidth) {
                    currentLine += ' ' + words[i];
                } else {
                    lines.push(currentLine);
                    currentLine = words[i];
                }
            }
            lines.push(currentLine);
            return lines;
        };

        let asciiTable = "";
        
        const separator = "|" + colWidths.map(w => "-".repeat(w + 2)).join("|") + "|";

        contentRows.forEach((row, rowIndex) => {
            const wrappedCells = row.map((cell, i) => wrapText(cell || "", colWidths[i]));
            const rowHeight = Math.max(...wrappedCells.map(c => c.length));

            for (let i = 0; i < rowHeight; i++) {
                const line = "|" + wrappedCells.map((cellLines, colIndex) => {
                    const text = cellLines[i] || ""; 
                    return " " + text.padEnd(colWidths[colIndex], ' ') + " ";
                }).join("|") + "|";
                
                asciiTable += line + "\n";
            }

            if (rowIndex === 0) {
                asciiTable += separator + "\n";
            }
        });

        return asciiTable;
    };

    const cleanMarkdown = (text: string) => {
        if (!text) return "";
        let cleaned = text;

        cleaned = cleaned.replace(/((?:^\|.+?\|\n)+)/gm, (match) => "\n" + formatTableToASCII(match) + "\n");
        cleaned = cleaned.replace(/^#{1,6}\s+(.*)$/gm, '$1');
        cleaned = cleaned.replace(/^(\*{3,}|-{3,}|_{3,})$/gm, '');
        cleaned = cleaned.replace(/^(\s*)[-*+]\s+/gm, '$1• ');
        cleaned = cleaned.replace(/^(\s*)-\s\[ \]\s/gm, '$1☐ '); 
        cleaned = cleaned.replace(/^(\s*)-\s\[x\]\s/gm, '$1☑ ');
        cleaned = cleaned.replace(/(\*\*|__)(.*?)\1/g, '$2'); 
        cleaned = cleaned.replace(/(\*|_)(.*?)\1/g, '$2');
        cleaned = cleaned.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '$1'); 
        cleaned = cleaned.replace(/!\[([^\]]*)\]\(([^)]+)\)/g, '$1'); 
        cleaned = cleaned.replace(/`([^`]+)`/g, '$1'); 
        cleaned = cleaned.replace(/```[\s\S]*?\n([\s\S]*?)```/g, '$1'); 

        return cleaned.trim();
    };
    
    const formatHTMLForClipboard = (html: string) => {
        return html
            .replace(/<table/g, '<table border="1" cellspacing="0" cellpadding="5" style="border-collapse: collapse; width: 100%; border: 1px solid #000;"')
            .replace(/<th/g, '<th style="background-color: #f2f2f2; border: 1px solid #000; padding: 5px; text-align: left;"')
            .replace(/<td/g, '<td style="border: 1px solid #000; padding: 5px;"');
    };

    const handleCopy = (text: string, id: string) => {
        const element = document.getElementById(`msg-content-${id}`);
        
        const plainText = cleanMarkdown(text);
        const rawHtml = element ? element.innerHTML : plainText;
        const styledHtml = formatHTMLForClipboard(rawHtml);

        if (navigator.clipboard && navigator.clipboard.write && typeof ClipboardItem !== "undefined") {
            try {
                const textBlob = new Blob([plainText], { type: 'text/plain' });
                const htmlBlob = new Blob([styledHtml], { type: 'text/html' });
                const data =[new ClipboardItem({
                    'text/plain': textBlob,
                    'text/html': htmlBlob
                })];
                navigator.clipboard.write(data).then(() => {
                    setCopiedId(id);
                    setTimeout(() => setCopiedId(null), 2000);
                });
            } catch (err) {
                fallbackCopy(plainText, id);
            }
        } else {
            fallbackCopy(plainText, id);
        }
    };

    const fallbackCopy = (text: string, id: string) => {
        navigator.clipboard.writeText(text).then(() => {
            setCopiedId(id);
            setTimeout(() => setCopiedId(null), 2000);
        }).catch(err => console.error('Failed to copy: ', err));
    };

    const handleShare = async (): Promise<void> => {
        const chatId = chatGroupId ?? '';
        if (chatId === '' || typeof window === 'undefined') return;

        const { origin, pathname } = window.location;
        const shareUrl = `${origin}${pathname}?${CHAT_ID_PARAM}=${chatId}`;

        try {
            if (navigator?.clipboard?.writeText) {
                await navigator.clipboard.writeText(shareUrl);
            }
        } catch (error) {
            console.warn('Clipboard write failed:', error);
        }

        if (navigator?.share) {
            try {
                await navigator.share({
                    title: 'Clutch Bot Conversation',
                    text: shareUrl,
                    url: shareUrl
                });
            } catch (error) {
                console.warn('Native share failed:', error);
            }
        } else {
            alert('Chat link copied to clipboard.');
        }
    };

    const handleStop = () => {
        if (lastVisibleMsg && lastVisibleMsg.isStreaming) {
            const stream = activeStreams.get(lastVisibleMsg.id);
            let finalBuffer = "";
            
            if (stream) {
                finalBuffer = stream.buffer;
                stream.controller.abort();
                activeStreams.delete(lastVisibleMsg.id);
            } else {
                finalBuffer = lastVisibleMsg.versions[lastVisibleMsg.currentVersion] || "";
            }

            try {
                chatDBApi.completeStream(lastVisibleMsg.id, finalBuffer, lastVisibleMsg.currentVersion);
            } catch (e) {
                console.error("Failed to save stopped stream to DB", e);
            }
            
            setMessages(prev => {
                return prev.map(m => m.id === lastVisibleMsg.id ? { ...m, isStreaming: false } : m);
            });

            if (activeStreams.size === 0) {
                setIsStreaming(false);
                localStorage.removeItem(STORAGE_KEY);
            }
        }
    };

    const streamChatResponse = async (
        url: string,
        formData: FormData,
        token: string,
        signal: AbortSignal,
        callbacks: {
            onStart: (headers: Headers) => void,
            onTokenChunk: (chunk: string) => void,
            onMetadata: (data: any) => void,
            onDone: () => void,
            onError: (error: Error) => void,
        }
    ) => {
        try {
            const response = await fetch(url, { 
                method: 'POST', 
                headers: { 
                    'Authorization': `Bearer ${token}` 
                }, 
                body: formData,
                signal: signal
            });

            if (!response.ok || !response.body) {
                const errorBody = await response.json().catch(() => ({ message: `Request failed with status ${response.status}` }));
                throw new Error(errorBody.message || `Request failed with status ${response.status}`);
            }

            const contentType = response.headers.get("content-type");

            if (contentType && contentType.includes("application/json")) {
                const data = await response.json();
                if (data.chat_group_id) {
                    callbacks.onMetadata({ chat_group_id: data.chat_group_id });
                }
                callbacks.onTokenChunk(data.data || "No response received.");
                callbacks.onDone();
                return;
            }

            callbacks.onStart(response.headers);
            const reader = response.body.getReader();
            const decoder = new TextDecoder();
            let isDone = false;

            while (!isDone) {
                const { done, value } = await reader.read();
                isDone = done;
                if (value) {
                    callbacks.onTokenChunk(decoder.decode(value));
                }
            }
            callbacks.onDone();

        } catch (error: any) {
            callbacks.onError(error);
        }
    };

    const startEditing = (msg: any) => { 
        setEditingMessageId(msg.id); 
    };

    const cancelEditing = () => { 
        setEditingMessageId(null); 
    };

    const saveEdit = async (msgId: string, textToSave: string) => {
        const trimmed = textToSave.trim();
        if (!trimmed) return;
        
        setEditingMessageId(null);
        setIsStreaming(true);

        const idx = messages.findIndex(m => m.id === msgId);
        if (idx === -1) return;
        
        const nextIdx = idx + 1;
        const assistantId = (nextIdx < messages.length && messages[nextIdx].role === "assistant") 
            ? messages[nextIdx].id 
            : uuidv4();

        const userMsgToEdit = messages[idx];
        const newUserVersionIndex = userMsgToEdit.versions.length;
        
        let newAsstVersionIndex = 0;
        let isNewAssistantMessage = false;

        if (nextIdx < messages.length && messages[nextIdx].role === "assistant") {
            newAsstVersionIndex = messages[nextIdx].versions.length;
        } else {
            isNewAssistantMessage = true;
            newAsstVersionIndex = 0;
        }

        setMessages(prev => {
            const pIdx = prev.findIndex(m => m.id === msgId);
            if (pIdx === -1) return prev;
            
            let updatedMsgs = [...prev];
            const userMsg = { 
                ...updatedMsgs[pIdx], 
                versions: [...updatedMsgs[pIdx].versions, trimmed], 
                currentVersion: updatedMsgs[pIdx].versions.length 
            };
            updatedMsgs[pIdx] = userMsg;
            
            const pNextIdx = pIdx + 1;
            if (pNextIdx < updatedMsgs.length && updatedMsgs[pNextIdx].role === "assistant") {
                const asMsg = updatedMsgs[pNextIdx];
                updatedMsgs[pNextIdx] = { 
                    ...asMsg, 
                    versions:[...asMsg.versions, ""], 
                    currentVersion: asMsg.versions.length, 
                    isStreaming: true 
                };
            } else {
                updatedMsgs.splice(pNextIdx, 0, { 
                    id: assistantId, 
                    role: "assistant", 
                    versions: [""], 
                    currentVersion: 0, 
                    isStreaming: true,
                    branchMap: userMsg.branchMap 
                });
            }

            localStorage.setItem(STORAGE_KEY, JSON.stringify({ messages: updatedMsgs, chatGroupId }));
            return updatedMsgs;
        });

        try {
            await chatDBApi.addVersion(msgId, trimmed, newUserVersionIndex, false);

            if (!isNewAssistantMessage) {
                await chatDBApi.addVersion(assistantId, "", newAsstVersionIndex, true);
            } else {
                const currentBranchMap: Record<string, number> = {};
                messages.forEach(m => { currentBranchMap[m.id] = m.currentVersion; });
                currentBranchMap[msgId] = newUserVersionIndex;
                
                await chatDBApi.createMessages(chatGroupId || "", [{
                    message_id: assistantId,
                    role: "assistant",
                    content: "",
                    branch_map: currentBranchMap,
                    sequence_order: messages.length + 1,
                    is_streaming: true
                }]);
            }
        } catch (e) {
            console.error("Failed to save edit to DB", e);
        }

        await triggerAPI(trimmed, assistantId, chatGroupId || "", undefined, newAsstVersionIndex);
    };

    const changeVersion = (msgId: string, direction: 'prev' | 'next') => {
        const msgIndex = messages.findIndex(m => m.id === msgId);
        if (msgIndex === -1) return;

        const msg = messages[msgIndex];
        let newVersion = msg.currentVersion + (direction === 'next' ? 1 : -1);

        if (newVersion < 0 || newVersion >= msg.versions.length) return;

        const updatedMessages = [...messages];
        updatedMessages[msgIndex] = { ...msg, currentVersion: newVersion };

        const nextMsgIndex = msgIndex + 1;
        if (nextMsgIndex < messages.length && messages[nextMsgIndex].role === "assistant") {
            const assistantMsg = messages[nextMsgIndex];
            if (newVersion < assistantMsg.versions.length) {
                updatedMessages[nextMsgIndex] = { ...assistantMsg, currentVersion: newVersion };
            }
        }

        setMessages(updatedMessages);
        try {
            chatDBApi.updateCurrentVersion(msgId, newVersion);
            if (nextMsgIndex < messages.length && messages[nextMsgIndex].role === "assistant") {
                const assistantMsg = messages[nextMsgIndex];
                if (newVersion < assistantMsg.versions.length) {
                    chatDBApi.updateCurrentVersion(assistantMsg.id, newVersion);
                }
            }
        } catch (e) {
            console.error("Failed to update version pointer in DB", e);
        }
    };

    const triggerAPI = async (question: string, assistantMsgId: string, groupId: string, currentMessagesState?: any[], targetVersionIndex: number = 0, isRecovery: boolean = false) => {
        setIsStreaming(true);
        const controller = new AbortController();
        activeStreams.set(assistantMsgId, { buffer: "", controller });

        const token = localStorage.getItem(RHYBUS_ACCESS_TOKEN) || sessionStorage.getItem(RHYBUS_ACCESS_TOKEN) || "";
        const url = `${process.env.REACT_APP_ANEW_NLP_API_URL}/ask`;
        const formData = new FormData();
        formData.append('question', question);
        if (isRecovery) {
            formData.append('is_recovery', 'true');
        }
        const fetchUrl = new URL(url);
        if (groupId) fetchUrl.searchParams.append("chat_group_id", groupId);

        let hasReceivedFirstChunk = false;
        
        const updateStorageDirectly = (newText: string, isFinished: boolean = false, targetMsgId: string = assistantMsgId) => {
            try {
                const saved = localStorage.getItem(STORAGE_KEY);
                let savedMsgs = saved ? JSON.parse(saved).messages :[];
                if(savedMsgs.length === 0 && currentMessagesState) savedMsgs = currentMessagesState;

                const msgIndex = savedMsgs.findIndex((m: any) => m.id === targetMsgId);
                
                if (msgIndex >= 0) {
                    const msg = savedMsgs[msgIndex];
                    
                    const vIndex = msg.versions ? msg.versions.length - 1 : 0;
                    
                    if (!msg.versions) msg.versions = [""];
                    msg.versions[vIndex] = newText;
                    msg.isStreaming = !isFinished;
                    savedMsgs[msgIndex] = msg;
                    
                    localStorage.setItem(STORAGE_KEY, JSON.stringify({ 
                        messages: savedMsgs, 
                        chatGroupId: groupId 
                    }));
                }
            } catch (e) { console.error("LS update error", e); }
        };

        let lastSaveTime = 0;

        await streamChatResponse(
            fetchUrl.toString(),
            formData,
            token,
            controller.signal,
            {
                onStart: (headers) => {
                    const headerChatId = headers.get("X-Chat-Group-Id");
                    if (headerChatId && headerChatId !== groupId) {
                        setChatGroupId(headerChatId);
                    }
                },
                onMetadata: (data) => {
                    if (data.chat_group_id && data.chat_group_id !== groupId) {
                        setChatGroupId(data.chat_group_id);
                    }
                },
                onTokenChunk: (chunk) => {
                    const stream = activeStreams.get(assistantMsgId);
                    if (!stream) return;

                    if (!hasReceivedFirstChunk) {
                        hasReceivedFirstChunk = true;
                    }

                    stream.buffer += chunk;

                    const now = Date.now();
                    if (now - lastSaveTime > 1500) { 
                        updateStorageDirectly(stream.buffer, false, assistantMsgId);
                        lastSaveTime = now;
                    }
                },
                onDone: () => {
                    const stream = activeStreams.get(assistantMsgId);
                    const finalBuffer = stream ? stream.buffer : "";
                    activeStreams.delete(assistantMsgId);

                    updateStorageDirectly(finalBuffer, true, assistantMsgId);
                    try {
                        chatDBApi.completeStream(assistantMsgId, finalBuffer, targetVersionIndex); 
                    } catch (e) {
                        console.error("Failed to complete stream in DB", e);
                    }
                    if (activeStreams.size === 0) {
                        setIsStreaming(false);
                        localStorage.removeItem(STORAGE_KEY);
                    }

                    if (typeof window !== 'undefined') {
                        window.dispatchEvent(new CustomEvent('stream-finished', {
                            detail: { id: assistantMsgId, text: finalBuffer }
                        }));
                    }

                    if (isMounted.current) {
                        if (activeStreams.size === 0) {
                            setIsStreaming(false);
                        }
                        
                        setMessages(prev => prev.map(msg => 
                            msg.id === assistantMsgId 
                                ? { ...msg, versions: updateVersion(msg.versions, msg.versions.length - 1, finalBuffer), isStreaming: false, recoveryText: undefined } 
                                : msg
                        ));
                    }
                },
                onError: (error) => {
                    if (error.name === 'AbortError') {
                        activeStreams.delete(assistantMsgId);
                        if (isMounted.current && activeStreams.size === 0) {
                            setIsStreaming(false);
                        }
                        return; 
                    }

                    if (isPageUnloading) return; 

                    console.error("Streaming error:", error);
                    
                    const stream = activeStreams.get(assistantMsgId);
                    let finalBuffer = stream ? stream.buffer : "";
                    activeStreams.delete(assistantMsgId);
                    
                    finalBuffer += "\n\nSorry, I encountered an issue processing your request.";
                    
                    if (isMounted.current && activeStreams.size === 0) {
                        setIsStreaming(false);
                    }
                    
                    updateStorageDirectly(finalBuffer, true, assistantMsgId);
                    try {
                        chatDBApi.completeStream(assistantMsgId, finalBuffer, targetVersionIndex); 
                    } catch (e) {
                        console.error("Failed to complete stream in DB", e);
                    }
                    if (activeStreams.size === 0) {
                        setIsStreaming(false);
                        localStorage.removeItem(STORAGE_KEY);
                    }

                    if (typeof window !== 'undefined') {
                        window.dispatchEvent(new CustomEvent('stream-finished', {
                            detail: { id: assistantMsgId, text: finalBuffer }
                        }));
                    }

                    if (isMounted.current) {
                        setMessages(prev => prev.map(msg => {
                            if (msg.id === assistantMsgId) {
                                return { 
                                    ...msg, 
                                    versions: updateVersion(msg.versions, msg.versions.length - 1, finalBuffer), 
                                    isStreaming: false,
                                    recoveryText: undefined
                                 };
                            }
                            return msg;
                        }));
                    }
                }
            }
        );
    };

    const updateVersion = (versions: string[], index: number, val: string) => {
        const copy = [...versions];
        copy[index] = val;
        return copy;
    };

    const sendMessage = async () => {
        if (isCurrentBranchStreaming) return; 
        const trimmed = message.trim();
        if (!trimmed) return;

        let currentChatGroupId = chatGroupId;
        if (!currentChatGroupId) {
            isNewChatCreated.current = true;
            currentChatGroupId = uuidv4();
            setChatGroupId(currentChatGroupId);
        }

        const newUserMsgId = uuidv4();
        const assistantMsgId = uuidv4();

        setMessage("");
        setIsStreaming(true);
        scrollToNewMessage(newUserMsgId);

         const currentSequenceLength = messages.length;
        
        setMessages(prev => {
            const currentBranchMap: Record<string, number> = {};
            prev.forEach(m => {
                currentBranchMap[m.id] = m.currentVersion;
            });

            const newUserMsg: any = {
                id: newUserMsgId,
                role: "user",
                versions: [trimmed],
                currentVersion: 0,
                branchMap: currentBranchMap
            };
            
            const newAssistantMsg = {
                id: assistantMsgId,
                role: "assistant",
                versions: [""],
                currentVersion: 0,
                isStreaming: true,
                branchMap: currentBranchMap
            };

            const nextMessages =[...prev, newUserMsg, newAssistantMsg];
            localStorage.setItem(STORAGE_KEY, JSON.stringify({ messages: nextMessages, chatGroupId: currentChatGroupId }));
            return nextMessages;
        });

        try {
            const currentBranchMap: Record<string, number> = {};
            messages.forEach(m => { currentBranchMap[m.id] = m.currentVersion; });

            chatDBApi.createMessages(currentChatGroupId, [
                {
                    message_id: newUserMsgId,
                    role: "user",
                    content: trimmed,
                    branch_map: currentBranchMap,
                    sequence_order: currentSequenceLength,
                    is_streaming: false
                },
                {
                    message_id: assistantMsgId,
                    role: "assistant",
                    content: "",
                    branch_map: currentBranchMap,
                    sequence_order: currentSequenceLength + 1,
                    is_streaming: true
                }
            ]);
        } catch (e) {
            console.error("Failed to save new messages to DB", e);
        }

        await triggerAPI(trimmed, assistantMsgId, currentChatGroupId);
    };

    useEffect(() => {
        const onClearEvent = () => {
            handleClearHistory();
        };
        window.addEventListener("new-chat-or-clear-chat", onClearEvent);
        return () => {
            window.removeEventListener("new-chat-or-clear-chat", onClearEvent);
        };
    },[]);

    useEffect(() => {
         if (ChatfooterRef.current) {
            document.documentElement.style.setProperty("--chatbot-footer-height", `${ChatfooterRef.current.scrollHeight}px`)
        }
    },[message]);

    const messageList = useMemo(() => {
    if (visibleMessages.length === 0 && !isCurrentBranchLoading && !isCurrentBranchStreaming) {
        if (isLoading && chatGroupId) {
            return (
                <div className="ai-welcome-box">
                    <div className="chatbox-loader-spinner">
                        <img src={rhybusloader} alt="loader" />
                    </div>
                    <div className="ai-welcome-title">Loading...</div>
                </div>
            );
        } else {
            return (
                <div className="ai-welcome-box">
                    <div className="ai-welcome-title">Welcome!</div>
                    <div className="ai-welcome-subtitle">How can I help you today?</div>
                </div>
            );
        }
    }

        return visibleMessages.map(msg => {
            const currentText = (msg.versions && msg.versions[msg.currentVersion]) || "";

            return (
                <React.Fragment key={msg.id}>
                    {msg.role !== "user" && <p className="text-color-61 font-11 sora-semibold" tabIndex={0} aria-label="Clutch Bot">Clutch Bot</p>}
                    
                    {msg.role === "user" && editingMessageId === msg.id ? (
                        <EditableMessage 
                            initialText={currentText} 
                            onSave={(txt) => saveEdit(msg.id, txt)} 
                            onCancel={cancelEditing} 
                        />
                    ) : (
                        msg.role === "user" ? (
                            <div className="user-message-wrapper" id={`user-msg-${msg.id}`}>
                                <div 
                                    className="chat-messages sent-message text-color-12" 
                                    tabIndex={0} 
                                    role="region" 
                                    aria-label="Your message"
                                >
                                    {currentText}
                                </div>
                                <div className="user-msg-actions">
                                    <button 
                                        className="msg-action-btn" 
                                        onClick={() => handleCopy(currentText, msg.id)}
                                        title="Copy"
                                    >
                                        <span className={`font-18 ${copiedId === msg.id ? 'icon-rb-checked' : 'icon-rb-copy'}`}></span>
                                    </button>
                                    <button 
                                        className="msg-action-btn" 
                                        onClick={() => startEditing(msg)}
                                        title="Edit Message"
                                    >
                                        <span className="icon-rb-pencil font-18"></span>
                                    </button>
                                    {msg.versions.length > 1 && (
                                        <div className="version-nav">
                                            <span 
                                                className={`nav-arrow ${msg.currentVersion === 0 ? 'disabled' : ''}`}
                                                onClick={(e) => { e.stopPropagation(); changeVersion(msg.id, 'prev'); }}
                                            >
                                                &lt;
                                            </span>
                                            <span className="mx-1">{msg.currentVersion + 1} / {msg.versions.length}</span>
                                            <span 
                                                className={`nav-arrow ${msg.currentVersion === msg.versions.length - 1 ? 'disabled' : ''}`}
                                                onClick={(e) => { e.stopPropagation(); changeVersion(msg.id, 'next'); }}
                                            >
                                                &gt;
                                            </span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        ) : (
                            <div className="chat-messages received-message">
                                <div id={`msg-content-${msg.id}`}>
                                    <MarkdownRenderer content={currentText} isStreaming={msg.isStreaming} />
                                </div>
                                {!msg.isStreaming && currentText && (
                                    <div className="message-actions">
                                        <button 
                                            className="action-btn" 
                                            onClick={() => handleCopy(currentText, msg.id)}
                                            title="Copy to clipboard"
                                        >
                                            <span className={`icon-action ${copiedId === msg.id ? 'icon-rb-checked' : 'icon-rb-copy'}`}></span>
                                            {copiedId === msg.id ? 'Copied' : 'Copy'}
                                        </button>
                                        <button 
                                            className="action-btn" 
                                            onClick={() => handleShare()} 
                                            title="Share"
                                        >
                                            <span className="icon-action icon-rb-export"></span>
                                            Share
                                        </button>
                                    </div>
                                )}
                            </div>
                        )
                    )}
                </React.Fragment>
            );
        });
    },[visibleMessages, editingMessageId, copiedId, isCurrentBranchLoading, isCurrentBranchStreaming]); 

    const handleScroll = () => {
        if (listRef.current) {
            sessionStorage.setItem('clutch_bot_scroll_pos', listRef.current.scrollTop.toString());
        }
    };

    useEffect(() => {
        if (typeof window === 'undefined') return;

        const params = new URLSearchParams(window.location.search);
        const sharedChatId = params.get(CHAT_ID_PARAM) ?? sessionStorage.getItem(PENDING_CHAT_KEY) ?? '';
        if (sharedChatId === '') return;
        const token = localStorage.getItem(RHYBUS_ACCESS_TOKEN) ?? sessionStorage.getItem(RHYBUS_ACCESS_TOKEN) ?? '';

        if (token === '' || sharedChatId === chatGroupId) return;

        const fetchSharedChat = async () => {
            try {
                setIsStreaming(true);
                const res = await fetch(`${process.env.REACT_APP_ANEW_NLP_API_URL}/chat/shared/${sharedChatId}`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                if (!res.ok) throw new Error(`HTTP ${res.status}`);
                const data = await res.json();

                if (Array.isArray(data?.data) && data.data.length > 0) {
                    const formattedMessages: any[] = [];
                    let runningBranchMap: Record<string, number> = {};

                    const historyList = [...data.data].sort((a: any, b: any) => 
                        new Date(a?.created_at ?? 0).getTime() - new Date(b?.created_at ?? 0).getTime()
                    );

                    historyList.forEach((item: any, index: number) => {
                        const uId = `u-shared-${index}`;
                        const aId = `a-shared-${index}`;
                        formattedMessages.push({
                            id: uId, role: 'user', versions: [item?.query ?? ''], 
                            currentVersion: 0, branchMap: { ...runningBranchMap }
                        });
                        runningBranchMap[uId] = 0;
                        formattedMessages.push({
                            id: aId, role: 'assistant', versions: [item?.response ?? ''], 
                            currentVersion: 0, branchMap: { ...runningBranchMap }
                        });
                        runningBranchMap[aId] = 0;
                    });

                    setMessages(formattedMessages);
                    setChatGroupId(sharedChatId);
                    localStorage.setItem(STORAGE_KEY, JSON.stringify({ messages: formattedMessages, chatGroupId: sharedChatId }));

                    setTimeout(() => {
                        if (listRef?.current) listRef.current.scrollTop = listRef.current.scrollHeight;
                    }, 600);

                    sessionStorage.removeItem(PENDING_CHAT_KEY);
                    window.history?.replaceState({}, document.title, window.location.pathname);
                }
            } catch (error) {
                console.error('Failed to load shared chat:', error);
            } finally {
                setIsStreaming(false);
            }
        };
        fetchSharedChat();
    }, []);

    useEffect(() => {
        if (activeStreams.size > 0) {
            setIsStreaming(true);
            setMessages(prev => prev.map(msg => {
                const stream = activeStreams.get(msg.id);
                if (stream && stream.buffer) {
                    const targetIndex = msg.versions.length - 1;
                    const newVersions = [...msg.versions];
                    newVersions[targetIndex] = stream.buffer;
                    return { ...msg, versions: newVersions, isStreaming: true };
                }
                return msg;
            }));
        }
    }, []);

    useEffect(() => {
        const fetchDBHistory = async () => {
            if (chatGroupId && messages.length === 0) {               
                if (isNewChatCreated.current) {
                    isNewChatCreated.current = false;
                    return;
                }

                try {
                    const dbData = await chatDBApi.loadHistory(chatGroupId);
                    if (dbData?.data?.messages?.length > 0) {
                        let loadedMessages = dbData.data.messages;
                        
                        let localParsed: any = null;
                        try {
                            const saved = localStorage.getItem(STORAGE_KEY);
                            if (saved) localParsed = JSON.parse(saved);
                        } catch (e) {}

                        loadedMessages = loadedMessages.map((dbMsg: any) => {
                            if (dbMsg.isStreaming) {
                                const stream = activeStreams.get(dbMsg.id);
                                if (stream && stream.buffer) {
                                    dbMsg.versions[dbMsg.currentVersion] = stream.buffer;
                                } 
                                else if (localParsed && localParsed.chatGroupId === chatGroupId && localParsed.messages) {
                                    const localMsg = localParsed.messages.find((m: any) => m.id === dbMsg.id);
                                    if (localMsg && localMsg.versions) {
                                        const partialText = localMsg.versions[localMsg.currentVersion] || "";
                                        if (partialText) {
                                            dbMsg.recoveryText = partialText;
                                            dbMsg.versions[dbMsg.currentVersion] = partialText; 
                                        }
                                    }
                                }
                            }
                            return dbMsg;
                        });

                        setMessages(loadedMessages);
                        const stuckMessages = loadedMessages.filter((m: any) => m.isStreaming && !activeStreams.has(m.id));
                        
                        if (stuckMessages.length > 0) {
                            setIsStreaming(true);
                            setTimeout(() => {
                                stuckMessages.forEach((stuckMsg: any) => {
                                    const idx = loadedMessages.findIndex((m: any) => m.id === stuckMsg.id);
                                    const userMsg = idx > 0 ? loadedMessages[idx - 1] : null;

                                    if (userMsg && userMsg.role === 'user') {
                                        const question = userMsg.versions[userMsg.currentVersion];
                                        triggerAPI(question, stuckMsg.id, chatGroupId, loadedMessages, stuckMsg.currentVersion, true);
                                    }
                                });
                            }, 100);
                        } else if (activeStreams.size > 0) {
                            setIsStreaming(true);
                        }
                        setIsLoading(false);
                    } else {
                        setIsLoading(false);
                    }
                } catch (error) {
                    setIsLoading(false);
                    console.error("Failed to fetch chat history from DB", error);
                }
            }
        };
        fetchDBHistory();
    }, [chatGroupId]);

    return (
        <>
            <div className="aichat-body p-4" ref={listRef} onScroll={handleScroll}>
                
                {messageList}

                {isCurrentBranchLoading && (
                    <div className="d-flex mb-2 w-100 justify-content-center">
                        <div className="chatbox-rhybus-loader">
                            <div className="chatbox-loader-spinner"><img src={rhybusloader} alt="loader" /> </div>
                            <div className="text-color-2 font-14">Clutch Bot is working on it...</div>
                        </div>
                    </div>
                )}
            </div>
            <div className="aichat-footer px-4 pt-3 pb-4" ref={ChatfooterRef}>
                <ChatFooter
                    message={message}
                    onChangeMessage={setMessage}
                    onSend={sendMessage}
                    isLoading={isCurrentBranchStreaming} 
                    onStop={handleStop}
                />
            </div>
        </>
    );
}